import {
    InteractionRequiredAuthError,
    PublicClientApplication
} from '@azure/msal-browser';
import { sha256 } from 'js-sha256';

import { browser } from '../base/lib-meet-hour';

const AUTH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const TOKEN_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';
const GRAPH_USER_URL = 'https://graph.microsoft.com/v1.0/me';
const GRAPH_DRIVE_URL = 'https://graph.microsoft.com/v1.0/me/drive';
const SCOPE = 'Files.ReadWrite.AppFolder offline_access User.Read';
const AUTH_STRATEGY = {
    LEGACY_PKCE: 'legacy-pkce',
    LEGACY_PKCE_SERVER: 'legacy-pkce-server',
    MSAL: 'msal'
};

const ONE_DRIVE_ERROR = {
    MISSING_CLIENT_ID: 'ONEDRIVE_MISSING_CLIENT_ID',
    WEB_CRYPTO_UNAVAILABLE: 'ONEDRIVE_WEB_CRYPTO_UNAVAILABLE',
    POPUP_BLOCKED: 'ONEDRIVE_POPUP_BLOCKED',
    POPUP_CLOSED: 'ONEDRIVE_POPUP_CLOSED',
    OAUTH_TIMEOUT: 'ONEDRIVE_OAUTH_TIMEOUT',
    OAUTH_ERROR: 'ONEDRIVE_OAUTH_ERROR',
    OAUTH_CANCELLED: 'ONEDRIVE_OAUTH_CANCELLED',
    MISSING_AUTH_CODE: 'ONEDRIVE_MISSING_AUTH_CODE',
    TOKEN_EXCHANGE_FAILED: 'ONEDRIVE_TOKEN_EXCHANGE_FAILED',
    REFRESH_EXCHANGE_FAILED: 'ONEDRIVE_REFRESH_EXCHANGE_FAILED',
    TOKEN_INVALID: 'ONEDRIVE_TOKEN_INVALID',
    PROFILE_LOAD_FAILED: 'ONEDRIVE_PROFILE_LOAD_FAILED',
    DRIVE_USAGE_LOAD_FAILED: 'ONEDRIVE_DRIVE_USAGE_LOAD_FAILED'
};

const msalClientsByCacheKey = new Map();

/**
 * Builds OneDrive-specific error object with stable code and optional details.
 *
 * @param {string} code - OneDrive error code.
 * @param {string} details - Optional debug details.
 * @returns {Error}
 */
function createOneDriveError(code: string, details?: string) {
    const error = new Error(code);

    error.code = code;

    if (details) {
        error.details = details;
    }

    return error;
}

/**
 * Validates OneDrive client id and returns a trimmed value.
 *
 * @param {string} clientId - OneDrive application client id.
 * @returns {string}
 */
function ensureClientId(clientId: string) {
    if (typeof clientId !== 'string' || !clientId.trim()) {
        throw createOneDriveError(ONE_DRIVE_ERROR.MISSING_CLIENT_ID);
    }

    return clientId.trim();
}

/**
 * Resolves WebCrypto implementation for browser and Electron.
 *
 * @returns {Object|undefined}
 */
function getWebCrypto() {
    let runtimeCrypto;

    if (typeof globalThis === 'undefined') {
        runtimeCrypto = undefined;
    } else {
        runtimeCrypto = globalThis.crypto;
    }

    if (runtimeCrypto?.subtle && runtimeCrypto?.getRandomValues) {
        return runtimeCrypto;
    }

    const isElectronApp = typeof browser !== 'undefined'
        && typeof browser.isElectron === 'function'
        && browser.isElectron();

    if (isElectronApp && typeof window !== 'undefined') {
        // Resolve Node WebCrypto from Electron renderer when browser WebCrypto is unavailable.
        const electronCrypto = window?.require?.('crypto')?.webcrypto;

        if (electronCrypto?.subtle && electronCrypto?.getRandomValues) {
            if (runtimeCrypto) {
                return electronCrypto;
            }

            if (typeof runtimeCrypto === 'undefined') {
                try {
                    globalThis.crypto = electronCrypto;
                } catch (e) {
                    // Ignore assignment failures and use the resolved object directly.
                }
            }

            return electronCrypto;
        }
    }

    return runtimeCrypto;
}

/**
 * Encodes ArrayBuffer as URL-safe base64.
 *
 * @param {ArrayBuffer} arrayBuffer - Input bytes.
 * @returns {string}
 */
function base64UrlEncode(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';

    bytes.forEach(b => {
        binary += String.fromCharCode(b);
    });

    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
}

/**
 * Converts OAuth expiry seconds to absolute timestamp.
 *
 * @param {number} expiresIn - Expiry in seconds.
 * @returns {number}
 */
function getTokenExpiresAtTimestamp(expiresIn: number) {
    return new Date(Date.now() + (expiresIn * 1000)).getTime();
}

/**
 * Converts OneDrive scope string into an array expected by MSAL.
 *
 * @returns {Array<string>}
 */
function getScopeArray() {
    return SCOPE.split(' ').filter(Boolean);
}

/**
 * Returns default redirect URI for runtime origin.
 *
 * @returns {string}
 */
function getDefaultRedirectURI() {
    if (typeof window === 'undefined' || !window.location?.origin) {
        return '';
    }

    return `${window.location.origin}/static/oauth.html`;
}

/**
 * Resolves OneDrive auth strategy from config hint.
 *
 * @param {string} strategy - Optional configured strategy.
 * @returns {string}
 */
function resolveAuthStrategy(strategy?: string) {
    if (typeof strategy === 'string') {
        const normalized = strategy.toLowerCase();

        if (normalized === AUTH_STRATEGY.MSAL) {
            return AUTH_STRATEGY.MSAL;
        }

        if (normalized === AUTH_STRATEGY.LEGACY_PKCE_SERVER) {
            return AUTH_STRATEGY.LEGACY_PKCE_SERVER;
        }
    }

    return AUTH_STRATEGY.LEGACY_PKCE;
}

/**
 * Normalizes token response payload into OneDrive token object used by UI.
 *
 * @param {Object} tokenData - Token payload from OAuth server.
 * @param {string} fallbackRefreshToken - Existing refresh token fallback.
 * @returns {Object}
 */
function normalizeTokenResult(tokenData: Object = {}, fallbackRefreshToken?: string) {
    const normalizedTokenData = tokenData?.response || tokenData?.data || tokenData;
    const token = normalizedTokenData?.access_token || normalizedTokenData?.accessToken;
    const refreshToken
        = normalizedTokenData?.refresh_token || normalizedTokenData?.refreshToken || fallbackRefreshToken || '';
    const expiresAt = Number(normalizedTokenData?.expireDate || normalizedTokenData?.expires_at);
    const expiresIn = Number(normalizedTokenData?.expires_in || normalizedTokenData?.expiresIn || 3600);

    if (!token) {
        throw createOneDriveError(ONE_DRIVE_ERROR.TOKEN_EXCHANGE_FAILED, 'Token response missing access token');
    }

    return {
        token,
        rToken: refreshToken,
        expireDate: Number.isFinite(expiresAt) && expiresAt > 0
            ? expiresAt
            : getTokenExpiresAtTimestamp(expiresIn)
    };
}

/**
 * Maps MSAL browser errors to OneDrive stable error codes.
 *
 * @param {Error|Object} error - MSAL thrown error.
 * @param {string} fallbackCode - OneDrive fallback error code.
 * @returns {Error}
 */
function toOneDriveMsalError(error: Object, fallbackCode: string) {
    const errorCode = error?.errorCode;

    if (errorCode === 'user_cancelled') {
        return createOneDriveError(ONE_DRIVE_ERROR.OAUTH_CANCELLED);
    }

    if (errorCode === 'popup_window_error') {
        return createOneDriveError(ONE_DRIVE_ERROR.POPUP_BLOCKED, error?.message);
    }

    if (errorCode === 'monitor_window_timeout') {
        return createOneDriveError(ONE_DRIVE_ERROR.OAUTH_TIMEOUT, error?.message);
    }

    return createOneDriveError(fallbackCode, error?.message || errorCode);
}

/**
 * Creates or returns a cached MSAL browser client for OneDrive.
 *
 * @param {string} clientId - OneDrive application client id.
 * @param {string} redirectURI - Redirect URI for auth completion.
 * @returns {PublicClientApplication}
 */
function getMsalClient(clientId: string, redirectURI: string) {
    const cacheKey = `${clientId}::${redirectURI}`;

    if (!msalClientsByCacheKey.has(cacheKey)) {
        msalClientsByCacheKey.set(cacheKey, new PublicClientApplication({
            auth: {
                authority: 'https://login.microsoftonline.com/common',
                clientId,
                navigateToLoginRequestUrl: false,
                redirectUri: redirectURI
            },
            cache: {
                cacheLocation: 'sessionStorage',
                storeAuthStateInCookie: true
            }
        }));
    }

    return msalClientsByCacheKey.get(cacheKey);
}

/**
 * Performs OneDrive token acquisition using MSAL.js popup/silent flow.
 *
 * @param {string} clientId - OneDrive application client id.
 * @param {string} redirectURI - OAuth redirect URI.
 * @param {boolean} allowInteractive - Whether popup interaction is allowed.
 * @returns {Promise<Object>}
 */
async function authorizeWithMsal(clientId: string, redirectURI: string, allowInteractive: boolean): Promise<Object> {
    const resolvedClientId = ensureClientId(clientId);
    const resolvedRedirectURI = redirectURI || getDefaultRedirectURI();

    if (!resolvedRedirectURI) {
        throw createOneDriveError(ONE_DRIVE_ERROR.OAUTH_ERROR, 'Missing redirect URI for MSAL strategy');
    }

    const msalClient = getMsalClient(resolvedClientId, resolvedRedirectURI);

    await msalClient.initialize();

    const scopes = getScopeArray();
    let account = msalClient.getActiveAccount() || msalClient.getAllAccounts()[0];

    if (!account && allowInteractive) {
        try {
            const loginResult = await msalClient.loginPopup({
                prompt: 'select_account',
                scopes
            });

            account = loginResult?.account;
        } catch (error) {
            throw toOneDriveMsalError(error, ONE_DRIVE_ERROR.OAUTH_ERROR);
        }
    }

    if (!account) {
        throw createOneDriveError(ONE_DRIVE_ERROR.REFRESH_EXCHANGE_FAILED, 'No MSAL account available');
    }

    msalClient.setActiveAccount(account);

    try {
        const tokenResult = await msalClient.acquireTokenSilent({
            account,
            scopes
        });

        // MSAL browser manages refresh internally and does not expose refresh token.
        return {
            token: tokenResult.accessToken,
            rToken: '',
            expireDate: tokenResult.expiresOn?.getTime() || getTokenExpiresAtTimestamp(3600)
        };
    } catch (error) {
        if (allowInteractive && error instanceof InteractionRequiredAuthError) {
            try {
                const tokenResult = await msalClient.acquireTokenPopup({
                    account,
                    prompt: 'select_account',
                    scopes
                });

                return {
                    token: tokenResult.accessToken,
                    rToken: '',
                    expireDate: tokenResult.expiresOn?.getTime() || getTokenExpiresAtTimestamp(3600)
                };
            } catch (popupError) {
                throw toOneDriveMsalError(popupError, ONE_DRIVE_ERROR.OAUTH_ERROR);
            }
        }

        throw toOneDriveMsalError(
            error,
            allowInteractive ? ONE_DRIVE_ERROR.OAUTH_ERROR : ONE_DRIVE_ERROR.REFRESH_EXCHANGE_FAILED
        );
    }
}

/**
 * Encodes UTF-8 text into bytes.
 *
 * @param {string} input - Input text.
 * @returns {Uint8Array}
 */
function encodeUtf8ToBytes(input: string) {
    return new TextEncoder().encode(input);
}

/**
 * Creates PKCE code challenge for an OAuth verifier.
 *
 * @param {string} codeVerifier - PKCE code verifier.
 * @returns {Promise<string>}
 */
async function createCodeChallenge(codeVerifier: string) {
    const webCrypto = getWebCrypto();

    if (webCrypto?.subtle?.digest) {
        const data = encodeUtf8ToBytes(codeVerifier);
        const digest = await webCrypto.subtle.digest('SHA-256', data);

        return base64UrlEncode(digest);
    }

    const digest = sha256.arrayBuffer(codeVerifier);

    return base64UrlEncode(digest);
}

/**
 * Creates a random PKCE code verifier.
 *
 * @returns {string}
 */
function createCodeVerifier() {
    const webCrypto = getWebCrypto();

    if (!webCrypto?.getRandomValues) {
        throw createOneDriveError(ONE_DRIVE_ERROR.WEB_CRYPTO_UNAVAILABLE);
    }

    const random = new Uint8Array(64);

    webCrypto.getRandomValues(random);

    return base64UrlEncode(random.buffer);
}

/**
 * Checks whether the callback URL contains OneDrive OAuth code.
 *
 * @param {string} callbackUrl - URL received from oauth callback.
 * @returns {boolean}
 */
function hasOneDriveAuthCode(callbackUrl: string) {
    if (typeof callbackUrl !== 'string' || !callbackUrl) {
        return false;
    }

    try {
        const parsedUrl = new URL(callbackUrl);

        return Boolean(parsedUrl.searchParams.get('code'));
    } catch (error) {
        return false;
    }
}

/**
 * Checks whether the callback URL already contains final OneDrive token data.
 *
 * @param {string} callbackUrl - URL received from oauth callback.
 * @returns {boolean}
 */
function hasOneDriveAccessToken(callbackUrl: string) {
    if (typeof callbackUrl !== 'string' || !callbackUrl) {
        return false;
    }

    try {
        const parsedUrl = new URL(callbackUrl);

        return Boolean(parsedUrl.searchParams.get('access_token'));
    } catch (error) {
        return false;
    }
}

/**
 * Checks whether the callback URL contains the expected OAuth state value.
 *
 * @param {string} callbackUrl - URL received from oauth callback.
 * @param {string} expectedState - OAuth state generated before opening popup.
 * @returns {boolean}
 */
function hasMatchingOneDriveState(callbackUrl: string, expectedState: string) {
    if (typeof callbackUrl !== 'string' || !callbackUrl || typeof expectedState !== 'string' || !expectedState) {
        return false;
    }

    try {
        const parsedUrl = new URL(callbackUrl);

        return parsedUrl.searchParams.get('state') === expectedState;
    } catch (error) {
        return false;
    }
}

/**
 * Opens OAuth popup and resolves redirected URL received via postMessage.
 *
 * @param {string} authUrl - OAuth authorize URL.
 * @param {string} windowName - Popup window name.
 * @returns {Promise<string>}
 */
function authorize(
        authUrl: string,
        windowName: string,
        expectedState: string,
        existingPopupWindow?: Window
): Promise<string> {
    const isElectronApp = typeof browser !== 'undefined'
        && typeof browser.isElectron === 'function'
        && browser.isElectron();

    return new Promise((resolve, reject) => {
        let popup = null;
        let closeWatcher = null;
        let timeout = null;
        let resolved = false;
        let closedPollCount = 0;
        const cleanup = messageHandler => {
            window.removeEventListener('message', messageHandler);

            if (closeWatcher) {
                window.clearInterval(closeWatcher);
                closeWatcher = null;
            }

            if (timeout) {
                window.clearTimeout(timeout);
                timeout = null;
            }
        };

        const handleAuth = ({ data }) => {
            const hasMatchingWindowName = data?.windowName === windowName;
            const hasMatchingState = hasMatchingOneDriveState(data?.url, expectedState);
            const hasOAuthCode = hasOneDriveAuthCode(data?.url);
            const hasDirectAccessToken = hasOneDriveAccessToken(data?.url);
            const isElectronDeepLinkFallback = isElectronApp
                && data?.windowName === ''
                && (hasOAuthCode || hasDirectAccessToken);
            const isWebStateFallback = !isElectronApp
                && (hasOAuthCode || hasDirectAccessToken)
                && hasMatchingState;

            if (data?.type === 'onedrive-login'
                && (hasMatchingWindowName || isElectronDeepLinkFallback || isWebStateFallback)) {
                resolved = true;
                if (popup) {
                    try {
                        popup.close();
                    } catch (e) {
                        // Ignore COOP-related close errors; oauth callback payload is already received.
                    }
                }
                cleanup(handleAuth);
                resolve(data.url);
            }
        };

        window.addEventListener('message', handleAuth);

        if (existingPopupWindow && !existingPopupWindow.closed) {
            popup = existingPopupWindow;

            try {
                popup.name = windowName;
                popup.location.href = authUrl;
                popup.focus && popup.focus();
            } catch (e) {
                popup = window.open(authUrl, windowName);
            }
        } else {
            popup = window.open(authUrl, windowName);
        }

        if (!popup) {
            cleanup(handleAuth);
            reject(createOneDriveError(ONE_DRIVE_ERROR.POPUP_BLOCKED));

            return;
        }

        closeWatcher = window.setInterval(() => {
            if (resolved) {
                return;
            }

            if (popup.closed) {
                closedPollCount += 1;

                // Give postMessage a short grace period after popup close.
                if (closedPollCount >= 4) {
                    cleanup(handleAuth);
                    reject(createOneDriveError(ONE_DRIVE_ERROR.POPUP_CLOSED));
                }
            } else {
                closedPollCount = 0;
            }
        }, 500);

        timeout = window.setTimeout(() => {
            cleanup(handleAuth);
            reject(createOneDriveError(ONE_DRIVE_ERROR.OAUTH_TIMEOUT));
        }, 180000);
    });
}

/**
 * Performs OneDrive OAuth authorization flow for web/electron.
 *
 * @param {string} clientId - OneDrive application client id.
 * @param {string} redirectURI - OAuth redirect URI.
 * @returns {Promise<Object>}
 */
export async function _authorizeOneDrive(
        clientId: string,
        redirectURI: string,
        strategy?: string,
        options?: {
            popupWindow?: Window
        }
): Promise<Object> {
    const resolvedStrategy = resolveAuthStrategy(strategy);

    if (resolvedStrategy === AUTH_STRATEGY.MSAL) {
        return authorizeWithMsal(clientId, redirectURI, true);
    }

    const resolvedClientId = ensureClientId(clientId);
    const windowName = `onedrive_oauth_${Date.now()}`;
    const oauthState = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
    const codeVerifier = createCodeVerifier();
    const codeChallenge = await createCodeChallenge(codeVerifier);
    const params = new URLSearchParams({
        client_id: resolvedClientId,
        response_type: 'code',
        redirect_uri: redirectURI,
        response_mode: 'query',
        scope: SCOPE,
        state: oauthState,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
    });
    const authUrl = `${AUTH_URL}?${params.toString()}`;
    const returnUrl = await authorize(authUrl, windowName, oauthState, options?.popupWindow);
    const search = new URL(returnUrl).searchParams;
    const oauthError = search.get('error');
    const oauthErrorDescription = search.get('error_description');

    if (oauthError) {
        throw createOneDriveError(ONE_DRIVE_ERROR.OAUTH_ERROR, oauthErrorDescription || oauthError);
    }

    const directAccessToken = search.get('access_token');

    if (directAccessToken) {
        return normalizeTokenResult({
            access_token: directAccessToken,
            expires_at: search.get('expires_at'),
            expires_in: search.get('expires_in'),
            refresh_token: search.get('refresh_token') || ''
        });
    }

    const code = search.get('code');
    const returnedState = search.get('state');

    if (!code) {
        throw createOneDriveError(ONE_DRIVE_ERROR.MISSING_AUTH_CODE);
    }

    if (returnedState !== oauthState) {
        throw createOneDriveError(ONE_DRIVE_ERROR.OAUTH_ERROR, 'Invalid OAuth state');
    }

    const tokenBody = new URLSearchParams({
        client_id: resolvedClientId,
        code,
        redirect_uri: redirectURI,
        grant_type: 'authorization_code',
        code_verifier: codeVerifier,
        scope: SCOPE
    });
    const response = await fetch(TOKEN_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: tokenBody.toString()
    });

    if (!response.ok) {
        const errorBody = await response.text();

        throw createOneDriveError(
            ONE_DRIVE_ERROR.TOKEN_EXCHANGE_FAILED,
            `status=${response.status}, body=${errorBody}`
        );
    }

    const tokenData = await response.json();

    return normalizeTokenResult(tokenData);
}

/**
 * Exchanges OneDrive refresh token for a new access token.
 *
 * @param {string} clientId - OneDrive application client id.
 * @param {string} rToken - OneDrive refresh token.
 * @returns {Promise<Object>}
 */
export async function getNewAccessToken(
        clientId: string,
        rToken: string,
        strategy?: string
): Promise<Object> {
    const resolvedClientId = ensureClientId(clientId);
    const resolvedStrategy = resolveAuthStrategy(strategy);

    if (typeof rToken !== 'string' || !rToken.trim()) {
        if (resolvedStrategy === AUTH_STRATEGY.MSAL) {
            return authorizeWithMsal(resolvedClientId, getDefaultRedirectURI(), false);
        }

        throw createOneDriveError(ONE_DRIVE_ERROR.REFRESH_EXCHANGE_FAILED, 'Missing refresh token');
    }

    const refreshBody = new URLSearchParams({
        client_id: resolvedClientId,
        refresh_token: rToken,
        grant_type: 'refresh_token',
        scope: SCOPE
    });
    const response = await fetch(TOKEN_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: refreshBody.toString()
    });

    if (!response.ok) {
        throw createOneDriveError(ONE_DRIVE_ERROR.REFRESH_EXCHANGE_FAILED);
    }

    const tokenData = await response.json();

    return normalizeTokenResult(tokenData, rToken);
}

/**
 * Loads OneDrive display name for current token.
 *
 * @param {string} token - OneDrive access token.
 * @returns {Promise<string>}
 */
export function getDisplayName(token: string) {
    return fetch(GRAPH_USER_URL, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    }).then(async response => {
        if (response.status === 401 || response.status === 403) {
            throw createOneDriveError(ONE_DRIVE_ERROR.TOKEN_INVALID);
        }
        if (!response.ok) {
            throw createOneDriveError(ONE_DRIVE_ERROR.PROFILE_LOAD_FAILED);
        }

        const data = await response.json();

        return data.displayName || data.userPrincipalName || '';
    });
}

/**
 * Loads OneDrive storage usage for current token.
 *
 * @param {string} token - OneDrive access token.
 * @returns {Promise<Object>}
 */
export function getSpaceUsage(token: string) {
    return fetch(GRAPH_DRIVE_URL, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    }).then(async response => {
        if (response.status === 401 || response.status === 403) {
            throw createOneDriveError(ONE_DRIVE_ERROR.TOKEN_INVALID);
        }
        if (!response.ok) {
            throw createOneDriveError(ONE_DRIVE_ERROR.DRIVE_USAGE_LOAD_FAILED);
        }

        const data = await response.json();
        const used = data?.quota?.used || 0;
        const allocated = data?.quota?.total || 0;
        const remaining = data?.quota?.remaining || Math.max(allocated - used, 0);
        const ownerName = data?.owner?.user?.displayName || '';
        const ownerEmail = data?.owner?.user?.email || '';

        return {
            used,
            allocated,
            ownerEmail,
            ownerName,
            remaining
        };
    });
}

/**
 * Checks whether OneDrive integration is enabled for web/electron.
 *
 * @param {Object} state - Redux state.
 * @returns {boolean}
 */
export function isEnabled(state: Object) {
    const { onedrive = {} } = state['features/base/config'];

    return typeof onedrive.clientId === 'string' && onedrive.clientId.length > 0;
}
